<template>
  <b-modal id="mailing-campaign-modal" v-model="showing" size="xl" centered scrollable
    :title="editing ? 'Editar campaña' : 'Nueva campaña'"
    :no-close-on-backdrop="saving" :no-close-on-esc="saving" :hide-header-close="saving"
    @hidden="resetForm">
    <form id="mailing-campaign-form" class="campaign-editor" @submit.prevent="submitForm">
      <div class="editor-fields">
        <section class="editor-section">
          <div class="section-heading"><span>1</span><h3>Elige a quién escribir</h3></div>
          <label for="campaign-country">País de la campaña</label>
          <select id="campaign-country" v-model="form.pais_codigo" required :disabled="saving || audienceLocked">
            <option v-for="country in countries" :key="country.codigo" :value="country.codigo">{{ country.nombre }}</option>
          </select>
          <p class="field-help">El envío se limita a destinatarios de {{ countryName }}.</p>
          <p v-if="selectedCountry && !selectedCountry.habilitado" class="field-help">Puedes preparar la campaña. El envío estará disponible cuando se habilite este país.</p>
          <div class="audience-options">
            <label v-for="option in audienceOptions" :key="option.value" class="audience-option"
              :class="{ selected: form.audiencia === option.value, locked: audienceLocked }">
              <input v-model="form.audiencia" type="radio" name="audiencia" :value="option.value" :disabled="saving || audienceLocked">
              <b-icon :icon="option.icon" aria-hidden="true" />
              <span><strong>{{ option.label }}</strong><small>{{ audienceCount(option.value) }} disponibles</small></span>
            </label>
          </div>
          <p class="field-help" v-if="audienceLocked">El país y la audiencia quedan fijos al iniciar los envíos. Para cambiarlos, crea otra campaña.</p>
          <p class="field-help" v-else-if="form.audiencia === 'profesionales'">Profesionales no suspendidos, con correo habilitado y suscritos a marketing.</p>
          <p class="field-help" v-else>Clientes suscritos a marketing que tengan solicitudes en este país. Se excluyen clientes sin país identificado.</p>
        </section>

        <section class="editor-section">
          <div class="section-heading"><span>2</span><h3>Prepara tu mensaje</h3></div>
          <label for="campaign-name">Nombre de la campaña <small>Solo para uso interno</small></label>
          <input id="campaign-name" ref="nameInput" v-model="form.nombre" required maxlength="255" placeholder="Ej. Novedades de septiembre" :disabled="saving">
          <label for="campaign-subject">Asunto del correo</label>
          <input id="campaign-subject" v-model="form.asunto" required maxlength="255" placeholder="Lo que verán en su bandeja de entrada" :disabled="saving">
          <label for="campaign-html">Contenido HTML</label>
          <div class="html-upload">
            <b-icon icon="file-earmark-code" aria-hidden="true" />
            <div><strong>{{ fileName || (editing ? 'HTML de la campaña actual' : 'Sube el diseño de tu correo') }}</strong>
              <small>Archivo .html con los enlaces de seguimiento y desuscripción.</small></div>
            <input id="campaign-html" ref="htmlFileInput" type="file" accept=".html,text/html" @change="handleFileUpload" :disabled="saving || reading">
          </div>
          <p v-if="reading" class="field-help" role="status">Leyendo archivo…</p>
          <p v-if="fileError" class="field-error" role="alert">{{ fileError }}</p>
          <details class="html-help"><summary>Claves para personalizar el HTML</summary>
            <p>Incluye estas tres claves obligatorias:</p>
            <code v-for="key in requiredKeys" :key="key">{{ key }}</code>
            <p>Usa <code>_NOMBRE_DE_PERSONA_</code> para el nombre. En el asunto puedes usar <code>_CLIENTE_</code> o <code>_PROFESIONAL_</code>.</p>
          </details>
        </section>

        <section class="editor-section delivery-section">
          <div class="section-heading"><span>3</span><h3>Configura el envío</h3></div>
          <label for="campaign-rate">Correos por lote</label>
          <input id="campaign-rate" class="rate-input" v-model.number="form.tasa_de_envio" type="number" min="1" max="1000" step="1" required :disabled="saving">
          <p class="field-help">El sistema procesa un lote cada 30 minutos, entre las 10:00 y las 21:59 (hora de Chile).</p>
          <div v-if="!editing" class="pause-note"><b-icon icon="pause-circle" /> La campaña se creará pausada. Inicia el envío cuando esté lista.</div>
        </section>
      </div>

      <aside class="preview-panel">
        <div class="preview-heading"><span>Vista previa</span><span class="preview-badge">{{ form.audiencia === 'profesionales' ? 'Profesionales' : 'Clientes' }}</span></div>
        <div class="preview-envelope"><small>ASUNTO</small><strong>{{ form.asunto || 'El asunto de tu correo aparecerá aquí' }}</strong><span>Jobbing · {{ countryName }}</span></div>
        <iframe v-if="htmlContent" :srcdoc="htmlContent" sandbox="" title="Vista previa del correo" referrerpolicy="no-referrer"></iframe>
        <div v-else class="preview-empty"><b-icon icon="envelope" /><strong>Tu próximo correo empieza aquí</strong><p>Sube un HTML para revisar su contenido antes de guardar.</p></div>
      </aside>

    </form>
    <template #modal-footer>
      <div class="editor-footer">
        <p v-if="error" class="field-error" role="alert">{{ error }}</p>
        <div class="footer-actions"><button type="button" class="editor-secondary" @click="hide" :disabled="saving">Cancelar</button>
          <button type="submit" form="mailing-campaign-form" class="editor-primary" :disabled="saving || reading || !!fileError">{{ saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear campaña en pausa' }}</button></div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import axios from 'axios';

const requiredKeys = ['_URL_DE_DESUSCRIPCION_', '_URL_IMAGEN_TRACKEO_', '_URL_SITIO_WEB_'];
const blankForm = (country = 'CL') => ({ nombre: '', asunto: '', audiencia: 'clientes', pais_codigo: country, tasa_de_envio: 30 });

export default {
  name: 'ModalNuevoMail',
  props: {
    countries: { type: Array, default: () => [] },
    audiencesByCountry: { type: Object, default: () => ({}) },
    defaultCountry: { type: String, default: 'CL' },
  },
  data() {
    return {
      showing: false, saving: false, reading: false, editing: false, emailId: null,
      audienceLocked: false, form: blankForm(), htmlContent: '', htmlChanged: false,
      fileName: '', fileError: '', error: '', requiredKeys,
      fileReadVersion: 0,
      audienceOptions: [
        { value: 'clientes', label: 'Clientes', icon: 'people' },
        { value: 'profesionales', label: 'Profesionales', icon: 'briefcase' },
      ],
    };
  },
  computed: {
    selectedCountry() { return this.countries.find(country => country.codigo === this.form.pais_codigo); },
    countryName() { return this.selectedCountry ? this.selectedCountry.nombre : this.form.pais_codigo; },
  },
  methods: {
    audienceCount(audience) {
      const count = (this.audiencesByCountry[this.form.pais_codigo] || {})[audience];
      return count == null ? '—' : Number(count).toLocaleString('es-CL');
    },
    resetForm() {
      this.fileReadVersion += 1;
      this.form = blankForm(this.defaultCountry);
      this.editing = false;
      this.emailId = null;
      this.audienceLocked = false;
      this.htmlContent = '';
      this.htmlChanged = false;
      this.fileName = '';
      this.fileError = '';
      this.error = '';
      this.reading = false;
    },
    show(campaign) {
      this.resetForm();
      if (campaign) {
        this.editing = true;
        this.emailId = campaign.id;
        this.form = { nombre: campaign.nombre || '', asunto: campaign.asunto || '', audiencia: campaign.audiencia || 'clientes', pais_codigo: campaign.pais_codigo || 'CL', tasa_de_envio: campaign.tasa_de_envio == null ? 30 : campaign.tasa_de_envio };
        this.htmlContent = campaign.html || '';
        this.audienceLocked = !!campaign.segmentacion_bloqueada || Number(campaign.enviados || 0) > 0 || Number(campaign.estado) !== 0;
      }
      this.showing = true;
    },
    hide() { if (!this.saving) this.showing = false; },
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      const version = ++this.fileReadVersion;
      this.reading = false;
      this.fileError = '';
      if (!/\.html$/i.test(file.name)) {
        this.fileError = 'Selecciona un archivo con extensión .html.';
        event.target.value = '';
        return;
      }
      this.reading = true;
      try {
        const content = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('No se pudo leer el archivo. Intenta nuevamente.'));
          reader.readAsText(file);
        });
        if (version !== this.fileReadVersion) return;
        const missing = requiredKeys.filter(key => !content.includes(key));
        if (missing.length) throw new Error('Faltan claves obligatorias: ' + missing.join(', '));
        this.htmlContent = content;
        this.htmlChanged = true;
        this.fileName = file.name;
      } catch (error) {
        if (version === this.fileReadVersion) this.fileError = error.message;
      } finally {
        if (version === this.fileReadVersion) this.reading = false;
        event.target.value = '';
      }
    },
    async submitForm() {
      if (this.saving || this.reading || this.fileError) return;
      this.error = '';
      const rate = Number(this.form.tasa_de_envio);
      if (!this.selectedCountry) {
        this.error = 'Selecciona un país válido para la campaña.';
        return;
      }
      if (!this.form.nombre.trim() || !this.form.asunto.trim()) {
        this.error = 'Completa el nombre y el asunto de la campaña.';
        return;
      }
      if (!Number.isInteger(rate) || rate < 1 || rate > 1000) {
        this.error = 'Elige entre 1 y 1.000 correos por lote.';
        return;
      }
      if (!this.editing && !this.htmlContent) {
        this.error = 'Sube un archivo HTML para crear la campaña.';
        return;
      }
      const payload = { ...this.form, tasa_de_envio: rate };
      if (!this.editing || this.htmlChanged) payload.html_content = this.htmlContent;
      if (this.editing) payload.email_id = this.emailId;
      this.saving = true;
      try {
        const endpoint = this.editing ? 'editar_email_marketing' : 'nuevo_email_marketing';
        const response = await axios.post(window.hostname + 'api/superadmin/' + endpoint, payload);
        this.$notify({ group: 'foo', title: 'Campaña guardada', text: response.data.message, type: 'success' });
        this.$emit('re-init');
        this.showing = false;
      } catch (error) {
        this.error = (error.response && error.response.data && error.response.data.message) || 'No se pudo guardar. Revisa los datos e intenta nuevamente.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.campaign-editor { display: grid; grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr); gap: 28px; padding: 8px; color: #26374b; }
.editor-section + .editor-section { border-top: 1px solid #e7edf3; margin-top: 24px; padding-top: 24px; }
.section-heading { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
.section-heading > span { display: grid; place-items: center; width: 25px; height: 25px; border-radius: 50%; background: #eaf4fb; color: #1375a8; font-size: 12px; font-weight: 700; }
.section-heading h3 { font-size: 16px; font-weight: 700; margin: 0; }
.editor-fields label:not(.audience-option) { display: block; font-size: 13px; font-weight: 600; margin: 16px 0 7px; }
label small { font-weight: 400; color: #758397; margin-left: 5px; }
.editor-fields input:not([type=radio]):not([type=file]), .editor-fields select { width: 100%; border: 1px solid #d4dee9; border-radius: 8px; padding: 10px 12px; font-size: 14px; color: #24364b; }
.editor-fields input:focus-visible, .editor-fields select:focus-visible { outline: 2px solid #1686b8; outline-offset: 2px; }
.audience-options { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 16px; }
.audience-option { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; padding: 14px 10px; border: 1px solid #dce4ed; border-radius: 10px; cursor: pointer; margin: 0; }
.audience-option.selected { background: #f0f8fd; border-color: #218bb8; }
.audience-option.locked { cursor: default; }
.audience-option input { accent-color: #137da9; }
.audience-option strong, .audience-option small { display: block; }
.audience-option strong { font-size: 13px; }
.audience-option small { font-size: 11px; color: #65768c; margin: 3px 0 0; }
.field-help { color: #64758b; font-size: 12px; margin: 9px 0 0; line-height: 1.6; }
.html-upload { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; background: #f7f9fc; border: 1px dashed #c4d3e2; border-radius: 10px; padding: 16px; }
.html-upload > .b-icon { font-size: 24px; color: #4683a6; }
.html-upload div { flex: 1; min-width: 180px; overflow-wrap: anywhere; }
.html-upload strong, .html-upload small { display: block; font-size: 12px; }
.html-upload small { color: #6a7c90; margin-top: 3px; }
.html-upload input { font-size: 12px; max-width: 100%; }
.html-help { margin-top: 12px; font-size: 12px; color: #64758b; }
.html-help summary { cursor: pointer; color: #32769b; }
.html-help p { margin: 10px 0; }
.html-help > code { display: block; margin: 4px 0; overflow-wrap: anywhere; }
.editor-fields .rate-input { max-width: 130px; }
.pause-note { display: flex; gap: 8px; align-items: flex-start; font-size: 12px; background: #fff8e7; color: #86611a; padding: 12px; border-radius: 8px; margin-top: 14px; }
.pause-note .b-icon { flex-shrink: 0; margin-top: 2px; }
.preview-panel { border: 1px solid #e0e7f0; border-radius: 12px; background: #f6f8fc; overflow: hidden; display: flex; flex-direction: column; min-height: 420px; }
.preview-heading { display: flex; justify-content: space-between; gap: 8px; align-items: center; padding: 14px 16px; font-size: 12px; font-weight: 600; }
.preview-badge { border: 1px solid #cbd9e8; border-radius: 20px; padding: 3px 9px; font-size: 11px; }
.preview-envelope { background: white; border-block: 1px solid #e0e7f0; padding: 18px; }
.preview-envelope small, .preview-envelope strong, .preview-envelope span { display: block; }
.preview-envelope small { color: #8290a3; font-size: 10px; letter-spacing: 1px; }
.preview-envelope strong { font-size: 14px; margin: 7px 0; overflow-wrap: anywhere; }
.preview-envelope span { font-size: 11px; color: #758397; }
.preview-panel iframe { width: 100%; flex: 1; border: 0; min-height: 460px; background: white; }
.preview-empty { flex: 1; display: flex; align-items: center; justify-content: center; flex-direction: column; text-align: center; padding: 40px; color: #7b8ca2; }
.preview-empty .b-icon { font-size: 42px; margin-bottom: 20px; color: #a4b8cc; }
.preview-empty strong { font-size: 14px; color: #566d86; }
.preview-empty p { font-size: 12px; margin-top: 8px; }
.editor-footer { width: 100%; margin: 0; }
.footer-actions { display: flex; justify-content: flex-end; gap: 10px; }
.editor-primary, .editor-secondary { border: 1px solid #d4dee9; border-radius: 8px; padding: 10px 16px; font-size: 13px; font-weight: 600; background: white; color: #435872; }
.editor-primary { color: white; background: #147fab; border-color: #147fab; }
button:disabled { opacity: .55; cursor: not-allowed; }
.field-error { color: #b53232; font-size: 13px; margin: 10px 0; }
@media (max-width: 800px) { .campaign-editor { grid-template-columns: 1fr; padding: 0; } .preview-panel { min-height: 250px; } .preview-panel iframe { min-height: 380px; } }
</style>

<style>
/* BootstrapVue portals these elements into body. Keep every override local to
   this modal, which inherits the admin's zoom and sits above its navigation. */
#mailing-campaign-modal___BV_modal_outer_ {
  z-index: 10040 !important;
}
#mailing-campaign-modal___BV_modal_outer_ > .modal-backdrop {
  /* Viewport units shrink with body zoom; percentages fill the fixed viewport. */
  width: 100%;
  height: 100%;
}
#mailing-campaign-modal .modal-dialog {
  width: calc(100% - 32px);
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
}
#mailing-campaign-modal .modal-content {
  border-radius: 12px;
}
#mailing-campaign-modal .modal-footer {
  flex-shrink: 0;
  border-top-color: #e7edf3;
  padding: 16px 24px;
}
</style>
